import React, { useEffect, useMemo, useRef } from 'react';
import { Alert, Spin } from 'antd';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PrintPreviewChrome } from '@/components/print/PrintPreviewChrome';
import NCRPrintView from '@/components/quality/NCRPrintView';
import { qualityService, type NCRRecord } from '@/services/quality.service';
import { useSystemSettings } from '@/hooks/useSettings';
import { useUsers } from '@/hooks/useUsers';
import employeeService, { employeeKeys } from '@/services/employee.service';
import { approvalService } from '@/services/approval.service';
import { ENTITY_TYPE_NCR_RECORD } from '@/constants/approval-entity-types';

const NCRPrintPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: settings = [] } = useSystemSettings();
  const didTriggerPrint = useRef(false);

  const { data: record, isLoading, isError, error } = useQuery({
    queryKey: ['ncr-print', id],
    queryFn: async () => {
      if (!id) throw new Error('Missing id');
      return (await qualityService.getNCRById(id)) as NCRRecord;
    },
    enabled: !!id,
  });

  const { data: users = [] } = useUsers({ is_active: true });
  const { data: employees = [] } = useQuery({
    queryKey: employeeKeys.list(),
    queryFn: () => employeeService.list(),
    staleTime: 60_000,
  });

  const { data: approvalSigners = [], isFetched: signersFetched } = useQuery({
    queryKey: ['ncr-print-signers', id],
    queryFn: async () => {
      if (!id) return [];
      try {
        return await approvalService.getPrintSigners(ENTITY_TYPE_NCR_RECORD, id);
      } catch {
        return [];
      }
    },
    enabled: !!id,
  });

  const personLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of users as any[]) {
      const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
      map[u.id] = name || u.email || u.username || u.id;
    }
    for (const e of employees as any[]) {
      if (map[e.id]) continue;
      const name =
        `${e.employee_number || ''} ${e.first_name || ''} ${e.last_name || ''}`.trim() ||
        e.id;
      map[e.id] = name;
      if (e.user_id && !map[e.user_id]) {
        map[e.user_id] = name;
      }
    }
    return map;
  }, [users, employees]);

  const companySettings = useMemo(
    () => settings.filter((setting) => setting.category === 'company'),
    [settings]
  );

  const companyInfo = useMemo(() => {
    const getSetting = (key: string) =>
      companySettings.find((setting) => setting.setting_key === key)?.setting_value?.trim() ?? '';

    const name = getSetting('company_name') || 'Company Name';
    const legalName = getSetting('company_legal_name') || name;
    const addressParts = [
      getSetting('company_address'),
      getSetting('company_address_line1'),
      getSetting('company_city'),
      getSetting('company_emirate_code'),
      getSetting('company_country_code'),
    ].filter(Boolean);

    return {
      name,
      legalName,
      address: addressParts.join(', ') || 'Company Address',
      logo: getSetting('company_logo') || undefined,
    };
  }, [companySettings]);

  const back = () => {
    // Print was opened in a new tab — close it; don't dump the user into the app shell.
    if (window.opener && !window.opener.closed) {
      window.close();
      window.setTimeout(() => {
        if (!window.closed) {
          if (id) navigate(`/qms/ncrs/${id}`, { replace: true });
          else navigate('/qms/ncrs', { replace: true });
        }
      }, 300);
      return;
    }
    if (id) {
      navigate(`/qms/ncrs/${id}`, { replace: true });
      return;
    }
    navigate('/qms/ncrs', { replace: true });
  };

  const readyToPrint = Boolean(record) && signersFetched;

  useEffect(() => {
    if (!readyToPrint || didTriggerPrint.current) return;

    didTriggerPrint.current = true;

    const handleAfterPrint = () => {
      window.removeEventListener('afterprint', handleAfterPrint);
      back();
    };

    window.addEventListener('afterprint', handleAfterPrint);
    const timeoutId = window.setTimeout(() => {
      window.print();
    }, 450);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [readyToPrint]);

  if (!id) {
    return <Navigate to="/qms/ncrs" replace />;
  }

  if (isLoading || !readyToPrint) {
    return (
      <PrintPreviewChrome title="NCR - print preview" onBack={back}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 120 }}>
          <Spin size="large" />
        </div>
      </PrintPreviewChrome>
    );
  }

  if (isError || !record) {
    return (
      <PrintPreviewChrome title="NCR - print preview" onBack={back}>
        <div style={{ padding: 48 }}>
          <Alert
            type="error"
            showIcon
            message="Could not load NCR"
            description={(error as Error)?.message || 'Check the record and your permissions.'}
          />
        </div>
      </PrintPreviewChrome>
    );
  }

  return (
    <PrintPreviewChrome title={`NCR ${record.ncr_number} - print preview`} onBack={back}>
      <div className="print-doc-a4">
        <NCRPrintView
          record={record}
          companyInfo={companyInfo}
          personLabels={personLabels}
          approvalSigners={approvalSigners}
        />
      </div>
    </PrintPreviewChrome>
  );
};

export default NCRPrintPage;
